"use client";

import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import {
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { WorkingSteps } from "@/components/tools/physics-waves/quantity-field";
import { t } from "@/lib/i18n/interpolate";
import { CHART_INK } from "@/lib/tool-kit/palette";
import {
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  type BandSet,
  type ColorId,
  colorDef,
  decodeBands,
  NO_BAND,
  seriesContaining,
} from "@/lib/tool-kit/physics-waves/resistor-code";

/** The strings the resistor view needs; the widget's own strings object satisfies it. */
export interface ResistorViewStrings {
  colorNames: Record<ColorId, string>;
  noBand: string;
  bandNames: {
    digit1: string;
    digit2: string;
    digit3: string;
    multiplier: string;
    tolerance: string;
    tempco: string;
  };
  bandColumn: string;
  colorColumn: string;
  meaningColumn: string;
  digitMeaning: string;
  multiplierMeaning: string;
  toleranceMeaning: string;
  tempcoMeaning: string;
  resistance: string;
  tolerance: string;
  range: string;
  tempco: string;
  series: string;
  noSeries: string;
  working: string;
  workingLine: string;
  bandsHeading: string;
  figureLabel: string;
  figureDescription: string;
}

interface DrawnBand {
  color: ColorId;
  gapBefore: boolean;
}

const STEP = 36;
const GAP = 38;
const BAND_WIDTH = 18;

/** The bands left to right, with a gap before the tolerance (and temperature) band. */
function drawnBands(bands: BandSet): DrawnBand[] {
  const out: DrawnBand[] = [
    ...bands.digits.map((color) => ({ color, gapBefore: false })),
    { color: bands.multiplier, gapBefore: false },
  ];
  if (bands.tolerance !== NO_BAND) {
    out.push({ color: bands.tolerance, gapBefore: true });
  }
  if (bands.tempco !== undefined) {
    out.push({ color: bands.tempco, gapBefore: false });
  }
  return out;
}

export function ResistorFigure({
  bands,
  strings,
}: {
  bands: BandSet;
  strings: ResistorViewStrings;
}) {
  const drawn = drawnBands(bands);
  const names = drawn.map((b) => strings.colorNames[b.color]).join(", ");
  const width = drawn.reduce(
    (sum, band, i) => sum + (i === 0 ? 0 : STEP) + (band.gapBefore ? GAP : 0),
    0
  );
  let x = 280 - width / 2 - BAND_WIDTH / 2;
  return (
    <ChartSvg
      className="mx-auto block h-auto w-full max-w-xl"
      description={t(strings.figureDescription, { colors: names })}
      height={150}
      id="rcc-figure"
      label={t(strings.figureLabel, { colors: names })}
      width={560}
    >
      <line
        stroke="#8a8a8a"
        strokeLinecap="round"
        strokeWidth={6}
        x1={24}
        x2={536}
        y1={75}
        y2={75}
      />
      <rect
        fill="#e3cf9f"
        height={86}
        rx={30}
        stroke="#8a7a55"
        strokeWidth={2}
        width={360}
        x={100}
        y={32}
      />
      {drawn.map((band, i) => {
        x += i === 0 ? 0 : STEP + (band.gapBefore ? GAP : 0);
        return (
          <rect
            fill={colorDef(band.color).hex}
            height={86}
            // The bands are positional: their order is the reading order.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional bands
            key={i}
            stroke={CHART_INK.text}
            strokeOpacity={0.35}
            strokeWidth={1}
            width={BAND_WIDTH}
            x={x}
            y={32}
          />
        );
      })}
    </ChartSvg>
  );
}

function bandRows(
  bands: BandSet,
  strings: ResistorViewStrings,
  fmt: NumberFormatter
): string[][] {
  const roles = [
    strings.bandNames.digit1,
    strings.bandNames.digit2,
    strings.bandNames.digit3,
  ];
  const rows: string[][] = bands.digits.map((id, i) => [
    roles[i],
    strings.colorNames[id],
    t(strings.digitMeaning, { digit: String(colorDef(id).digit ?? 0) }),
  ]);
  rows.push([
    strings.bandNames.multiplier,
    strings.colorNames[bands.multiplier],
    t(strings.multiplierMeaning, {
      factor: fmt(10 ** colorDef(bands.multiplier).multExp, {
        maximumFractionDigits: 2,
        useGrouping: false,
      }),
    }),
  ]);
  const tolerance = decodeBands(bands).tolerance;
  rows.push([
    strings.bandNames.tolerance,
    bands.tolerance === NO_BAND
      ? strings.noBand
      : strings.colorNames[bands.tolerance],
    t(strings.toleranceMeaning, { percent: fmt(tolerance) }),
  ]);
  if (bands.tempco !== undefined) {
    rows.push([
      strings.bandNames.tempco,
      strings.colorNames[bands.tempco],
      t(strings.tempcoMeaning, {
        ppm: fmt(colorDef(bands.tempco).tempco ?? 0),
      }),
    ]);
  }
  return rows;
}

/** The figure, the headline numbers, the working and the band-by-band table. */
export function ResistorOutcome({
  bands,
  strings,
  fmt,
  sig,
}: {
  bands: BandSet;
  strings: ResistorViewStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const decoded = decodeBands(bands);
  const series = seriesContaining(decoded.ohms);
  const ohms = (value: number) => withSiPrefix(fmt, value, "Ω", sig);
  return (
    <div className="space-y-4">
      <ResistorFigure bands={bands} strings={strings} />
      <StatGrid>
        <StatCard
          emphasis
          label={strings.resistance}
          value={ohms(decoded.ohms)}
        />
        <StatCard
          label={strings.tolerance}
          value={t(strings.toleranceMeaning, {
            percent: fmt(decoded.tolerance),
          })}
        />
        <StatCard
          label={strings.range}
          value={`${ohms(decoded.min)} – ${ohms(decoded.max)}`}
        />
        {decoded.tempco === null ? null : (
          <StatCard
            label={strings.tempco}
            value={t(strings.tempcoMeaning, { ppm: fmt(decoded.tempco) })}
          />
        )}
        <StatCard
          label={strings.series}
          value={series.length > 0 ? series.join(", ") : strings.noSeries}
        />
      </StatGrid>
      <WorkingSteps
        heading={strings.working}
        lines={[
          t(strings.workingLine, {
            digits: String(decoded.significand),
            factor: fmt(10 ** decoded.multExp, {
              maximumFractionDigits: 2,
              useGrouping: false,
            }),
            ohms: `${fmt(decoded.ohms, { maximumSignificantDigits: 8 })} Ω`,
          }),
        ]}
      />
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.bandsHeading}</h3>
        <DataTable
          head={[
            strings.bandColumn,
            strings.colorColumn,
            strings.meaningColumn,
          ]}
          rows={bandRows(bands, strings, fmt)}
        />
      </div>
    </div>
  );
}
